import React from 'react';
import { OverlayTrigger, Tooltip } from '@/components/ui';
import type { ComplianceStatus } from '../services/vendorService';
import { shortDate as formatDate } from '../features/vendors/utils/format';

/**
 * HE-M18 — the red warning triangle shown beside a vendor's WCB and insurance expiry dates.
 *
 * This is ONE component used in the vendors list, vendor detail, the PO detail page and the
 * admin screen, because the story asks for the indicator "wherever they appear". A per-page
 * reimplementation is how four screens end up disagreeing about whether a vendor is covered.
 *
 * Indicator only this sprint: no notification, no hard block, no disabled controls. HE-M08
 * separately blocks ACTIVATION on invalid coverage — that is a different rule, enforced
 * server-side in VendorService, and nothing here should be read as implementing it.
 */

export interface ExpiryIndicatorProps {
  compliance?: ComplianceStatus | null;
  /** Show the reason next to the triangle rather than only in the tooltip. */
  showLabel?: boolean;
  className?: string;
}

/**
 * Builds the reason text. "Missing" and "expired" are deliberately distinct: a coordinator
 * chasing a certificate that was never uploaded does something different from one chasing a
 * renewal, and collapsing them into "invalid" loses that.
 */
function describe(compliance: ComplianceStatus): string[] {
  const reasons: string[] = [];
  if (compliance.wcbMissing) reasons.push('WCB clearance is missing');
  else if (compliance.wcbExpired) reasons.push(`WCB clearance expired ${formatDate(compliance.wcbExpiresAt)}`);
  if (compliance.insuranceMissing) reasons.push('Liability insurance is missing');
  else if (compliance.insuranceExpired)
    reasons.push(`Liability insurance expired ${formatDate(compliance.insuranceExpiresAt)}`);
  return reasons;
}

export const ExpiryIndicator: React.FC<ExpiryIndicatorProps> = ({
  compliance,
  showLabel = false,
  className = '',
}) => {
  if (!compliance?.hasIssue) return null;

  const reasons = describe(compliance);
  const label = reasons.join(' · ');

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`expiry-${label}`}>{label}</Tooltip>}
    >
      <span
        className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-danger ${className}`}
        // The icon alone is not an accessible name, and colour alone is not a signal.
        role="img"
        aria-label={label}
      >
        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
        {showLabel && <span className="tw-text-sm">{label}</span>}
      </span>
    </OverlayTrigger>
  );
};

type CheckState = 'valid' | 'expired' | 'missing' | 'unknown';

function checkLabel(document: string, state: CheckState, date: string | null): string {
  if (state === 'missing') return `${document}: not provided`;
  if (state === 'expired') return `${document}: expired ${formatDate(date)}`;
  if (state === 'unknown') return `${document}: not known`;
  return date ? `${document}: valid to ${formatDate(date)}` : `${document}: valid`;
}

const CHECK_ICON: Record<CheckState, string> = {
  valid: 'bi-check-square-fill',
  expired: 'bi-x-square-fill',
  missing: 'bi-square',
  unknown: 'bi-dash-square',
};

const ComplianceCheck: React.FC<{ document: string; short: string; state: CheckState; date: string | null }> = ({
  document,
  short,
  state,
  date,
}) => {
  const label = checkLabel(document, state, date);
  const tone = state === 'valid' ? 'tw-text-success' : state === 'unknown' ? 'tw-text-content-muted' : 'tw-text-danger';

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`compliance-${short}-${state}`}>{label}</Tooltip>}>
      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-whitespace-nowrap">
        <i className={`bi ${CHECK_ICON[state]} ${tone}`} role="img" aria-label={label} />
        <span>{short}</span>
      </span>
    </OverlayTrigger>
  );
};

export interface ComplianceChecksProps {
  compliance?: ComplianceStatus | null;
  className?: string;
}

/**
 * WCB and liability insurance as one tick box each, for list views.
 *
 * Replaces a run-on sentence ("WCB clearance expired 12 Mar 2026 · Liability insurance is
 * missing") that was hard to scan down a column: two fixed boxes let you read one document
 * per position instead of parsing prose, and a covered vendor stops being a wall of text.
 *
 * Deliberately NOT an <input type="checkbox">. Compliance is derived server-side from the
 * uploaded documents and their expiry dates, so a real checkbox would invite someone to
 * toggle a verdict they cannot set from a list. The missing-vs-expired distinction the
 * tooltip keeps still matters - chasing a certificate never uploaded is different work from
 * chasing a renewal - so the two failures get different icons rather than one empty box.
 */
export const ComplianceChecks: React.FC<ComplianceChecksProps> = ({ compliance, className = '' }) => {
  const state = (missing?: boolean, expired?: boolean): CheckState => {
    if (!compliance) return 'unknown';
    if (missing) return 'missing';
    if (expired) return 'expired';
    return 'valid';
  };

  return (
    <span className={`tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 ${className}`}>
      <ComplianceCheck
        document="WCB clearance"
        short="WCB"
        state={state(compliance?.wcbMissing, compliance?.wcbExpired)}
        date={compliance?.wcbExpiresAt ?? null}
      />
      <ComplianceCheck
        document="Liability insurance"
        short="Insurance"
        state={state(compliance?.insuranceMissing, compliance?.insuranceExpired)}
        date={compliance?.insuranceExpiresAt ?? null}
      />
    </span>
  );
};

export interface ExpiryDateProps {
  /** The expiry date to render. */
  value: string | null;
  expired?: boolean;
  missing?: boolean;
  className?: string;
}

/**
 * One expiry date with its own indicator, for the vendor detail Documents tab where WCB and
 * insurance are shown on separate rows and each needs its own verdict.
 */
export const ExpiryDate: React.FC<ExpiryDateProps> = ({
  value,
  expired = false,
  missing = false,
  className = '',
}) => {
  const problem = expired || missing;
  const text = missing ? 'Not provided' : formatDate(value);
  const reason = missing ? 'Not provided' : expired ? `Expired ${formatDate(value)}` : '';

  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-1 ${problem ? 'tw-text-danger tw-font-medium' : ''} ${className}`}
    >
      {problem && (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`expiry-date-${value ?? 'none'}`}>{reason}</Tooltip>}>
          <i className="bi bi-exclamation-triangle-fill" role="img" aria-label={reason} />
        </OverlayTrigger>
      )}
      <span>{text}</span>
    </span>
  );
};

export default ExpiryIndicator;
